// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useCallback } from 'react';

import type { ElementId, PartCategoryId, WorkOrderId } from '@evolvemep/api-ts';
// oxlint-disable-next-line no-restricted-imports
import { useSearchParams } from 'react-router';

import type { ALL_MODULE_ROUTES } from 'app/ModuleRoutes';
import { isNil, isNotNil } from 'helpers/isNotNil';
import type { SELECTED_WORK_ORDER_QUERY_PARAM_KEY } from 'modules/Shop/EvolveFabrication/FabricationWorkOrders';

/**
 * Map of Route to query parameters
 * Original map of routes is in `ALL_MODULE_ROUTES`
 */
type RouteParams = {
  '/fabrication': {
    bendId: ElementId;
    [SELECTED_WORK_ORDER_QUERY_PARAM_KEY]: WorkOrderId;
  };
  '/work-orders/:workOrderId': {
    bendId: ElementId;
  };
  '/catalog-setup': {
    categoryId: PartCategoryId;
  };
};

/** Used to interface with `ModuleRoutes` query parameters in a type-safe way. */
export const useRouteSearchParam = <
  R extends keyof typeof ALL_MODULE_ROUTES & keyof RouteParams,
  P extends keyof RouteParams[R] & string,
>(
  _route: R,
  param: P,
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const value = useMemo(() => {
    const v = searchParams.get(param);
    return isNil(v) ? null : (String(v) as RouteParams[R][P]);
  }, [searchParams, param]);

  const onChange = useCallback(
    (
      v:
        | ((previousValue: RouteParams[R][P] | null) => (RouteParams[R][P] & string) | null)
        | (RouteParams[R][P] & string)
        | null,
    ) =>
      setSearchParams((prev) => {
        const newValue = typeof v === 'function' ? v(prev.get(param) as RouteParams[R][P] | null) : v;
        if (isNotNil(newValue)) {
          prev.set(param, String(newValue));
        } else {
          prev.delete(param);
        }
        return prev;
      }),
    [param, setSearchParams],
  );

  return [value, onChange] as const;
};
